import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useLocation } from "@entry/navigation";

import { TextInput } from "@components/Input";
import { OnboardingLayoutProps } from "@components/OnboardingLayout";
import { VERIFIED_TERMS_OF_USE_URL } from "@constants";
import { AnalyticsEvent, Routes } from "@ctypes";
import { useEntryIntakeStore } from "@entry";
import { EntryStepLayout } from "@entry/EntryStepLayout";
import { EntryBody, EntryLink } from "@entry/ui";
import { useErrorBanner, useInitiateLoginWithPhone, useRegisterWithPhone } from "@hooks";
import { Translations, i18n } from "@i18n";
import { constants } from "@layout";
import { Analytics } from "@services";
import {
  clearDeeplinkPromoCode,
  formatPhoneNumber,
  getGraphQLErrorExtensions,
  isGraphQLErrorWithCode,
  sanitizePhoneNumber,
  toE164,
} from "@utils";
import { validationSchema } from "./content";
import { PhoneLoginInputRouteFormValues } from "./types";

type PhoneLoginInputLocationState = {
  signup?: boolean;
  phoneNumber?: string;
  autoSubmit?: boolean;
};

export const PhoneLoginInputRoute = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { showErrorBanner } = useErrorBanner();

  const { email: intakeEmail, promotionCode } = useEntryIntakeStore.getState().intake;

  const { phoneNumber: phoneNumberFromLink, autoSubmit } =
    (location.state as PhoneLoginInputLocationState) ?? {};

  // The get-started journey lands here with signup intent and a collected
  // email; account creation is passwordless (phone + SMS code), like mobile.
  const isSignup = Boolean((location.state as PhoneLoginInputLocationState)?.signup && intakeEmail);

  const [initiateLoginWithPhone, { loading: initiateLoading }] = useInitiateLoginWithPhone();
  const [registerWithPhone, { loading: registerLoading }] = useRegisterWithPhone();

  const loading = initiateLoading || registerLoading;

  const {
    register,
    setValue,
    watch,
    handleSubmit: handleFormSubmit,
    formState: { errors, isValid },
  } = useForm<PhoneLoginInputRouteFormValues>({
    defaultValues: {
      phoneNumber: phoneNumberFromLink ? formatPhoneNumber(phoneNumberFromLink) : "",
    },
    mode: "all",
    resolver: yupResolver(validationSchema),
  });

  const phoneNumberController = register("phoneNumber");

  const onChangePhoneNumber = (value: string) => {
    const formattedPhoneNumber = formatPhoneNumber(value);

    setValue("phoneNumber", formattedPhoneNumber, constants.form);
  };

  const startLogin = async (phoneNumber: string) => {
    await initiateLoginWithPhone({
      variables: {
        phoneNumber,
      },
    });

    navigate(Routes.PhoneLoginVerification, { state: { phoneNumber } });
  };

  const startRegistration = async (phoneNumber: string) => {
    const { data } = await registerWithPhone({
      variables: {
        email: intakeEmail,
        phoneNumber,
        promotionCode: promotionCode || undefined,
      },
    });

    if (!data?.registerWithPhone) {
      throw new Error("registerWithPhone failed");
    }

    // The account now carries the code, so the remembered copy has done its job. Dropped here so it
    // cannot attach itself to a second signup on this browser.
    clearDeeplinkPromoCode();

    // The lead conversion: the account exists from here on. Fired after the
    // mutation resolves so the phone-already-registered fallback to login,
    // handled by the caller, is never counted as a signup. Email and phone go
    // to the dataLayer only, for ad-conversion matching - see Analytics.track.
    Analytics.track(AnalyticsEvent.SignUp, undefined, {
      email_address: intakeEmail,
      phone_number: toE164(phoneNumber),
    });

    navigate(Routes.PhoneLoginVerification, { state: { phoneNumber, email: intakeEmail } });
  };

  const handleSubmit = async ({
    phoneNumber: phoneNumberValue,
  }: PhoneLoginInputRouteFormValues) => {
    const phoneNumber = sanitizePhoneNumber(phoneNumberValue);

    if (isSignup) {
      try {
        await startRegistration(phoneNumber);
      } catch (registrationError: any) {
        if (isGraphQLErrorWithCode(registrationError, "AU0045")) {
          const extensions = getGraphQLErrorExtensions(registrationError, "AU0045");
          navigate(Routes.PartnerExistingAccount, {
            state: {
              partnerName: extensions?.partnerName as string | undefined,
              loginMethod: extensions?.loginMethod as string | undefined,
              phoneNumber,
            },
          });
          return;
        }

        // The phone number may already have an account – fall back to login.
        try {
          await startLogin(phoneNumber);
        } catch (error: any) {
          if (isGraphQLErrorWithCode(error, "AU0024")) {
            navigate(Routes.EmailCodeVerification, { state: { phoneNumber } });
          } else {
            const message = error?.graphQLErrors?.[0]?.message;
            showErrorBanner(message);
          }
        }
      }
      return;
    }

    try {
      await startLogin(phoneNumber);
    } catch (error: any) {
      if (isGraphQLErrorWithCode(error, "AU0024")) {
        navigate(Routes.EmailCodeVerification, { state: { phoneNumber } });
        return;
      }

      // AU0023: no account exists for this number. If we collected an email
      // in the entry flow, register the user passwordless instead.
      if (isGraphQLErrorWithCode(error, "AU0023") && intakeEmail) {
        try {
          await startRegistration(phoneNumber);
          return;
        } catch {
          // fall through to the error banner
        }
      }

      const message = error?.graphQLErrors?.[0]?.message;
      showErrorBanner(message);
    }
  };

  // A deep link carrying a number sends the code on arrival and moves straight to the code
  // screen. The ref keeps StrictMode's double-invoked effect from sending two.
  const autoSubmittedRef = useRef(false);

  useEffect(() => {
    if (!autoSubmit || !phoneNumberFromLink || autoSubmittedRef.current || !isValid) return;

    autoSubmittedRef.current = true;
    void handleFormSubmit(handleSubmit)();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSubmit, phoneNumberFromLink, isValid]);

  const props: OnboardingLayoutProps = {
    title: i18n.t(Translations.PhoneNumberRoutePhoneNumberTitle),
    cta: {
      primary: {
        title: i18n.t(Translations.SignUpRoutePrimaryCTA),
        onClick: handleFormSubmit(handleSubmit),
        disabled: !isValid,
        loading,
      },
    },
    footer: (
      <EntryBody
        $align="center"
        style={{ fontSize: 12, color: "rgba(255, 255, 255, 0.55)", maxWidth: 420 }}
      >
        {i18n.t(Translations.VerifiedFooter1)}
        <EntryLink href={VERIFIED_TERMS_OF_USE_URL} target="_blank" rel="noopener noreferrer">
          {i18n.t(Translations.VerifiedFooterHighlight)}
        </EntryLink>
        {i18n.t(Translations.VerifiedFooter2)}
      </EntryBody>
    ),
  };

  return (
    <EntryStepLayout {...props}>
      <TextInput
        {...phoneNumberController}
        label={i18n.t(Translations.PhoneNumberRoutePhoneNumberLabel)}
        placeholder={i18n.t(Translations.PhoneNumberRoutePhoneNumberPlaceholder)}
        value={watch("phoneNumber")}
        onChangeText={onChangePhoneNumber}
        error={errors.phoneNumber?.message}
      />
    </EntryStepLayout>
  );
};
